import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  type FormListSelectionChangedArgs,
  FormListSelectionOption,
} from 'donor-elf-web/components/form/list-selection';
import BulkActionModel, {
  type BulkActionCustomRemoveCustomProperty,
  type BulkActionCustomSetCustomProperty,
  type BulkActionResponseIncludesCustomPropertyInfo,
} from 'donor-elf-web/models/bulk-action';
import ContactsCache, {
  type ContactCustomPropertyInfo,
} from 'donor-elf-web/services/contacts-cache';
import type { BulkActionsContactsChangedArgs } from '../..';

interface ComponentArgs {
  isGroupsProperty: boolean;
  propertyInfo?: ContactCustomPropertyInfo; // will be undefined if isGroupsProperty is true
  contactIds: string[];
  onContactsChanged: (args: BulkActionsContactsChangedArgs) => void;
  onClose: () => void;
}

type Stages = 'ask' | 'add' | 'remove';

export default class ContactBulkActionsEditPropertyExistingListTypeComponent extends Component<ComponentArgs> {
  @service declare contactsCache: ContactsCache;
  @service declare store: Store;

  @tracked stage: Stages = 'ask';
  @tracked removeOptionsNotSelected = false;

  bulkAction?: BulkActionModel;
  @tracked waitingOnBulkActionToComplete = false;
  @tracked saveError = false;
  @tracked saving = false;
  @tracked validationErrorMessage?: string;

  selectedListOptions: FormListSelectionOption[] = [];

  get propertyLabel() {
    return this.args.isGroupsProperty ? 'Groups' : this.args.propertyInfo!.l;
  }

  get selectionLabel() {
    return this.args.isGroupsProperty ? 'Groups' : 'Selections';
  }

  get canDeleteProperty() {
    return !this.args.isGroupsProperty;
  }

  get listOptions(): FormListSelectionOption[] {
    if (this.args.isGroupsProperty) {
      return this.contactsCache.sortedGroups.map((x) => {
        return new FormListSelectionOption({ val: x, selected: false });
      });
    } else {
      return this.args.propertyInfo!.o!.map((x) => {
        return new FormListSelectionOption({ val: x, selected: false });
      });
    }
  }

  @action
  handleListSelectionsChanged(args: FormListSelectionChangedArgs) {
    this.selectedListOptions = args.selectedListOptions;
  }

  @action
  setStage(stage: Stages) {
    this.stage = stage;
  }

  @action
  toggleRemoveOptionsNotSelected() {
    this.removeOptionsNotSelected = !this.removeOptionsNotSelected;
  }

  @action
  async save() {
    if (this.selectedListOptions.length === 0) {
      this.validationErrorMessage = 'Must select at least 1 option.';
      return;
    } else if (this.validationErrorMessage != null) {
      this.validationErrorMessage = undefined;
    }
    this.bulkAction = this.store.createRecord('bulk-action');
    this.bulkAction.actionType = 'set_custom_property';
    this.saving = true;
    let label = this.args.isGroupsProperty
      ? 'Groups'
      : this.args.propertyInfo!.l;
    let value: any = {};
    for (let x of this.selectedListOptions) {
      value[x.val] = true;
    }
    let custom: BulkActionCustomSetCustomProperty = {
      contact_ids: this.args.contactIds,
      new_property: false,
      l: label,
      v: value,
      t: 'l',
    };
    if (this.stage === 'remove') {
      custom.list_options_action = 'remove';
    } else if (this.stage === 'add' && !this.removeOptionsNotSelected) {
      custom.list_options_action = 'add';
    }
    this.bulkAction.custom = custom;
    await this.bulkAction.save();
    this.waitingOnBulkActionToComplete = true;
  }

  @action
  async handleBulkActionStatusChange(bulkAction: BulkActionModel) {
    if (bulkAction.status === 'completed') {
      try {
        let updatedCustomPropertyInfo = (
          bulkAction.response! as BulkActionResponseIncludesCustomPropertyInfo
        ).custom_property_info;
        if (updatedCustomPropertyInfo) {
          this.contactsCache.updateCustomPropertyInfo(
            updatedCustomPropertyInfo,
          );
        }
        await bulkAction.destroyRecord();
        if (this.args.contactIds.length < 250) {
          await this.contactsCache.reloadSpecificContacts(this.args.contactIds);
        } else {
          await this.contactsCache.loadContacts({ forceLoad: true });
        }
        this.args.onContactsChanged({
          contactIds: this.args.contactIds,
          bulkActionType: bulkAction.actionType,
        });
        this.args.onClose();
      } catch (error: any) {
        this.saveError = true;
      }
    } else if (bulkAction.status === 'error') {
      this.saveError = true;
    }
  }

  @action
  async handleDelete() {
    this.bulkAction = this.store.createRecord('bulk-action');
    this.bulkAction.actionType = 'remove_custom_property';
    this.saving = true;
    (this.bulkAction.custom as BulkActionCustomRemoveCustomProperty) = {
      contact_ids: this.args.contactIds,
      property_label: this.args.propertyInfo!.l,
    };
    await this.bulkAction.save();
    this.waitingOnBulkActionToComplete = true;
  }
}
